import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, KeyRound } from 'lucide-react';

type RecoveryState = 'waiting' | 'ready' | 'success' | 'expired';

export default function UpdatePassword() {
  const [state, setState] = useState<RecoveryState>('waiting');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stateRef = useRef<RecoveryState>('waiting');

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    let isMounted = true;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

    const setReady = () => {
      if (!isMounted || stateRef.current === 'ready' || stateRef.current === 'success') return;
      setState('ready');
      if (pollTimer) clearInterval(pollTimer);
      if (timeoutTimer) clearTimeout(timeoutTimer);
    };

    // Listen for auth events (may fire before or after bootstrap)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady();
        return;
      }
      if (event === 'SIGNED_IN' && session?.user && stateRef.current === 'waiting') {
        setReady();
        return;
      }
    });

    const bootstrapRecoverySession = async () => {
      const url = new URL(window.location.href);
      const code = url.searchParams.get('code');

      // 1. Try code exchange (may fail silently if already consumed)
      if (code) {
        try {
          await supabase.auth.exchangeCodeForSession(code);
        } catch {
          // Code already consumed — that's fine
        }
      }

      // 2. Also try hash fragment tokens (Safari / implicit flow)
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const type = hashParams.get('type');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (type === 'recovery' && accessToken && refreshToken) {
        try {
          await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
        } catch {
          // Fall through to polling
        }
      }

      // Clean up URL
      if (code || accessToken) {
        window.history.replaceState({}, '', url.pathname);
      }

      // 3. Check session immediately
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && isMounted) {
          setReady();
          return;
        }
      } catch {
        // Fall through to polling
      }

      // 4. If no recovery params at all, expire faster
      if (!code && !accessToken) {
        if (isMounted) setState('expired');
        return;
      }

      // 5. Poll for session (handles slow auth init on iOS)
      pollTimer = setInterval(async () => {
        if (!isMounted || stateRef.current !== 'waiting') {
          if (pollTimer) clearInterval(pollTimer);
          return;
        }
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            setReady();
          }
        } catch {
          // keep polling
        }
      }, 250);
    };

    // Hard timeout — stop waiting after 10s
    timeoutTimer = setTimeout(() => {
      if (isMounted && stateRef.current === 'waiting') {
        setState('expired');
        if (pollTimer) clearInterval(pollTimer);
      }
    }, 10000);

    void bootstrapRecoverySession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (pollTimer) clearInterval(pollTimer);
      if (timeoutTimer) clearTimeout(timeoutTimer);
    };
  }, []);

  const goToForgotMode = async () => {
    await supabase.auth.signOut();
    navigate('/auth?mode=forgot', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) return;

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      toast({ title: t('auth.passwordUpdateFailed'), description: error.message, variant: 'destructive' });
    } else {
      setState('success');
    }

    setLoading(false);
  };

  return (
    <main className="flex min-h-[100dvh] flex-col items-center justify-center bg-background p-5">
      <div className="w-full max-w-sm space-y-6">
        {state === 'waiting' && (
          <div className="space-y-3 rounded-2xl bg-card p-6 text-center shadow-sm">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              <KeyRound className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-foreground">{t('common.loading')}</p>
          </div>
        )}

        {state === 'ready' && (
          <div className="space-y-4 rounded-2xl bg-card p-6 shadow-sm">
            <div className="flex flex-col items-center gap-2 text-center">
              <KeyRound className="h-9 w-9 text-primary" />
              <h1 className="font-display text-xl font-bold text-foreground tracking-tight">{t('auth.newPasswordTitle')}</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="new-password" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('auth.newPassword')}
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoFocus
                  className="h-11 rounded-xl border-0 bg-muted text-sm placeholder:text-muted-foreground/50"
                />
              </div>
              <Button type="submit" className="h-11 w-full rounded-xl text-sm font-semibold" disabled={loading}>
                {loading ? t('auth.updating') : t('auth.confirmButton')}
              </Button>
            </form>
          </div>
        )}

        {state === 'success' && (
          <div className="space-y-3 rounded-2xl bg-card p-6 text-center shadow-sm">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <p className="font-semibold text-foreground">{t('auth.passwordResetSuccess')}</p>
            <Button
              onClick={async () => {
                await supabase.auth.signOut();
                navigate('/auth', { replace: true });
              }}
              variant="outline"
              className="rounded-xl"
            >
              {t('auth.backToSignIn')}
            </Button>
          </div>
        )}

        {state === 'expired' && (
          <div className="space-y-3 rounded-2xl bg-card p-6 text-center shadow-sm">
            <AlertCircle className="mx-auto h-10 w-10 text-destructive" />
            <p className="font-medium text-foreground">{t('auth.sessionExpired')}</p>
            <Button onClick={goToForgotMode} className="rounded-xl">
              {t('auth.requestNewLink')}
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
